/**
 * Load a third-party script once, on demand, and resolve when it is usable.
 *
 * Exists so vendor scripts are not referenced from index.html on every page
 * load (CASA Q6): they are pulled only when a user action needs them, and only
 * from an origin the Content-Security-Policy allow-lists. Keeping this in one
 * place is also what lets script-src stay free of 'unsafe-inline'.
 */
const inflight = new Map<string, Promise<void>>()

export function loadExternalScript(src: string): Promise<void> {
  const existing = inflight.get(src)
  if (existing) return existing

  const promise = new Promise<void>((resolve, reject) => {
    const el = document.createElement('script')
    el.src = src
    el.async = true
    el.addEventListener('load', () => resolve(), { once: true })
    el.addEventListener(
      'error',
      () => {
        // Drop the cache entry so a later click retries rather than replaying
        // this rejection forever — a vendor outage must not disable the link
        // for the rest of the session.
        inflight.delete(src)
        el.remove()
        reject(new Error(`Failed to load script: ${src}`))
      },
      { once: true }
    )
    document.head.appendChild(el)
  })

  inflight.set(src, promise)
  return promise
}

/** Test-only: forget every in-flight/loaded src. */
export function __resetLoadedScriptsForTests(): void {
  inflight.clear()
}
